import React, { createContext, useContext } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ApiContext = createContext();

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};

export const ApiProvider = ({ children }) => {
  const { getToken } = useAuth();

  // CONFIGURE axios instance with authentication headers
  // Production: subdomain routing (api.cipher.insurtale.io)
  // Development: proxy via package.json to localhost:3001
  const apiClient = axios.create({
    baseURL: process.env.NODE_ENV === 'production'
      ? 'https://api.cipher.insurtale.io/api'
      : '/api',
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // INTERCEPT requests to add authentication token
  apiClient.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // HANDLE API response errors consistently
  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      console.error('API Error:', error.response?.data || error.message);
      return Promise.reject(error);
    }
  );

  // CREATE new secret via API
  const createSecret = async (secretData) => {
    try {
      const response = await apiClient.post('/secrets', secretData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to create secret');
    }
  };

  // RETRIEVE secret by ID and consume it
  const getSecret = async (secretId, passphrase = null) => {
    try {
      const params = passphrase ? { passphrase } : {};
      const response = await apiClient.get(`/secrets/${secretId}`, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to retrieve secret');
    }
  };

  // PREVIEW secret metadata without consuming
  const previewSecret = async (secretId) => {
    try {
      const response = await apiClient.get(`/secrets/${secretId}/preview`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to preview secret');
    }
  };

  // GENERATE random password with specified options
  const generatePassword = async (options) => {
    try {
      const response = await apiClient.post('/generate', options);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to generate password');
    }
  };

  // CHECK API health status
  const checkHealth = async () => {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Health check failed');
    }
  };

  const value = {
    createSecret,
    getSecret,
    previewSecret,
    generatePassword,
    checkHealth,
  };

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  );
};